import React, { useMemo } from 'react'
import moment from 'moment'
import { FormControlLabel, Hidden, makeStyles, Switch, Typography } from '@paintscout/ui'
import { EstimatorAvatar, useClientOptions, useUser, useUserPermissions, useUsers } from '@paintscout/ui-business'
import { formatCurrency, getCrews } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util'
import type { CalendarEvent } from 'paintscout'
import type { View } from 'react-big-calendar'
import { useProductionCalendar } from './ProductionCalendarContext'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    backgroundColor: theme.palette.common.white
  },
  totalsScroller: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'visible',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    flex: 1,
    '&::-webkit-scrollbar': {
      height: '4px'
    }
  },
  totalGroup: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'nowrap'
  },
  totalLabel: {
    color: theme.paintscout.palette.brand.Neutral.P50,
    fontWeight: 400
  },
  totalAmount: {
    color: theme.paintscout.palette.brand.Neutral.P30,
    fontWeight: 600
  },
  divider: {
    width: '1px',
    height: '20px',
    backgroundColor: theme.paintscout.palette.brand.Neutral.P85,
    flexShrink: 0
  },
  perUser: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0
  },
  perUserAmount: {
    color: theme.paintscout.palette.brand.Neutral.P30,
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'nowrap'
  },
  weekendsSwitch: {
    color: theme.paintscout.palette.brand.Neutral.P50,
    flexShrink: 0,
    margin: 0,
    '& label': {
      marginLeft: theme.spacing(1)
    }
  }
}))

interface ProductionCalendarFooterProps {
  view: View
  date: Date
  events: CalendarEvent[]
}

const getRangeForView = (date: Date, view: View) => {
  const m = moment(date)
  if (view === 'week') {
    return { start: m.clone().startOf('week').toDate(), end: m.clone().endOf('week').toDate() }
  }
  if (view === 'month') {
    return { start: m.clone().startOf('month').toDate(), end: m.clone().endOf('month').toDate() }
  }
  return { start: m.clone().startOf('day').toDate(), end: m.clone().endOf('day').toDate() }
}

export default function ProductionCalendarFooter(props: ProductionCalendarFooterProps) {
  const { view, date, events } = props
  const classes = useStyles()
  const { hideWeekends, setHideWeekends } = useProductionCalendar()
  const { options } = useClientOptions()
  const { users, userPictures } = useUsers()
  const { user } = useUser()
  const { permissionToReadAllDocuments } = useUserPermissions()
  const crews = getCrews({ options, includeTrashed: true })

  const { total, perManager } = useMemo(() => {
    const { start, end } = getRangeForView(date, view)
    const currentUserId = user?.user_id ? removeAuth0Prefix(user.user_id) : undefined
    const rangeEvents = events.filter((event) => {
      if (!event.start || !event.end) return false
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)
      if (!(eventStart <= end && eventEnd >= start)) return false
      if (!permissionToReadAllDocuments) {
        const crew = crews.find((c) => c._id === event.crew)
        const managerId = crew?.projectManager?.id
        if (!managerId || !currentUserId) return false
        return removeAuth0Prefix(managerId) === currentUserId
      }
      return true
    })

    const total = rangeEvents.reduce((sum, e) => sum + (e.deal?.value || 0), 0)

    const perManagerMap = new Map<string, number>()
    rangeEvents.forEach((event) => {
      const crew = crews.find((c) => c._id === event.crew)
      const managerId = crew?.projectManager?.id
      if (!managerId) return
      const value = event.deal?.value || 0
      perManagerMap.set(managerId, (perManagerMap.get(managerId) || 0) + value)
    })

    const perManager = Array.from(perManagerMap.entries())
      .map(([id, amount]) => ({ id, amount }))
      .sort((a, b) => b.amount - a.amount)

    return { rangeEvents, total, perManager }
  }, [events, date, view, crews, permissionToReadAllDocuments, user?.user_id])

  const totalLabel = view === 'month' ? 'Total month' : view === 'week' ? 'Total week' : 'Total'

  return (
    <div className={classes.root}>
      <div className={classes.totalsScroller}>
        <div className={classes.totalGroup}>
          <span className={classes.totalLabel}>{totalLabel}:</span>
          <span className={classes.totalAmount}>{formatCurrency({ options, value: total })}</span>
        </div>
        {perManager.length > 0 && <div className={classes.divider} />}
        <Hidden xsDown>
          {perManager.map(({ id, amount }) => {
            const user = users?.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(id))
            if (!user) return null
            return (
              <div key={id} className={classes.perUser}>
                <EstimatorAvatar
                  user={{
                    id: user.user_id,
                    firstName: user.user_metadata?.firstName,
                    lastName: user.user_metadata?.lastName
                  }}
                  userPictures={userPictures}
                  borderColor={user.user_metadata?.color}
                  width="22px"
                  height="22px"
                />
                <Typography className={classes.perUserAmount}>{formatCurrency({ options, value: amount })}</Typography>
              </div>
            )
          })}
        </Hidden>
      </div>
      <FormControlLabel
        className={classes.weekendsSwitch}
        label="Weekends"
        labelPlacement="start"
        control={<Switch checked={!hideWeekends} onChange={() => setHideWeekends(!hideWeekends)} />}
      />
    </div>
  )
}
